import PropTypes from 'prop-types'
import frLocale from 'apexcharts/dist/locales/fr.json'

export const bucketShape = PropTypes.shape({
  label: PropTypes.string,
  count: PropTypes.number,
  value: PropTypes.number
})

export const DS_COLORS = {
  primary: '#2cc675',
  info: '#3b82f6',
  warning: '#e35542',
  danger: '#b80000',
  neutralEmpty: '#ffffff',
  neutralDisabled: '#6b7280',
  neutralLowest: '#ebebeb'
}

export const AXIS_LABEL_STYLE = {
  colors: DS_COLORS.neutralDisabled,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '14px',
  fontWeight: 500
}

export const AXIS_TITLE_STYLE = { ...AXIS_LABEL_STYLE, color: DS_COLORS.neutralDisabled }

// Reset is disabled: it triggers a stale trailing zoomed event that races our re-sampling.
const TOOLBAR_TOOLS = {
  download: false,
  selection: false,
  zoom: true,
  zoomin: true,
  zoomout: true,
  pan: true,
  reset: false
}

export const buildChartBaseOptions = (type, chartId) => ({
  chart: {
    id: chartId,
    fontFamily: 'Roboto, sans-serif',
    type,
    locales: [frLocale],
    defaultLocale: 'fr',
    // Toolbar stays mounted (CSS-hidden in chart wrappers) so ChartInteractions can delegate clicks.
    toolbar: { show: true, autoSelected: 'pan', tools: TOOLBAR_TOOLS },
    // Disable wheel zoom so the chart never traps page scrolling (COL-637).
    zoom: { enabled: true, allowMouseWheelZoom: false, type: 'x', autoScaleYaxis: true }
  },
  xaxis: {
    labels: { style: AXIS_LABEL_STYLE, rotate: 0, rotateAlways: false, hideOverlappingLabels: false },
    // Hide the floating x-axis label that duplicates the date already shown in the tooltip header.
    tooltip: { enabled: false }
  },
  legend: { show: false },
  // Negative right padding aligns the grid right edge with the card.
  grid: {
    borderColor: DS_COLORS.neutralLowest,
    strokeDashArray: 4,
    padding: { right: -5 }
  }
})

const LABEL_PADDING_PX = 16
const labelMeasureCache = {}

const measureLabelWidth = (text, font) => {
  labelMeasureCache.canvas ||= document.createElement('canvas')
  const ctx = labelMeasureCache.canvas.getContext('2d')
  ctx.font = font
  return ctx.measureText(text).width
}

const sampleLabels = (labels, { containerWidth, font, range }) => {
  if (!containerWidth || labels.length === 0)
    return labels
  const start = range?.min ?? 0
  const end = range?.max ?? labels.length - 1
  const visibleCount = Math.max(1, end - start + 1)
  const longest = labels.reduce((acc, label) => label.length > acc.length ? label : acc, '')
  const labelWidth = measureLabelWidth(longest, font) + LABEL_PADDING_PX
  const maxLabels = Math.max(2, Math.floor(containerWidth / labelWidth))
  let sampled = [...labels]
  if (visibleCount > maxLabels) {
    const step = Math.ceil(visibleCount / maxLabels)
    sampled = labels.map((label, idx) => (idx - start) % step === 0 ? label : '')
  }
  // Hide the trailing label — it would overflow the grid right edge.
  if (visibleCount > 1)
    sampled[end] = ''
  return sampled
}

const AXIS_FONT = `${AXIS_LABEL_STYLE.fontWeight} ${AXIS_LABEL_STYLE.fontSize} ${AXIS_LABEL_STYLE.fontFamily}`

export const computeSampledLabels = (buckets, { containerWidth, range } = {}) =>
  sampleLabels(buckets.map(bucket => bucket.label), { containerWidth, font: AXIS_FONT, range })

/* ApexCharts injects this HTML inside its own tooltip wrapper; Tailwind JIT
 * cannot scan classes inside template strings, so inline styles match the Flowbite mockup. */
const TOOLTIP_WRAPPER_STYLE = 'background:#fff;border-radius:4px;'
  + 'box-shadow:0 4px 6px -1px rgba(0,0,0,.1),0 2px 4px -2px rgba(0,0,0,.05);'
  + 'overflow:hidden;font-family:Roboto,sans-serif'
const TOOLTIP_HEADER_STYLE = 'background:#f3f4f6;border-bottom:1px solid #e5e7eb;padding:10px 12px'
const TOOLTIP_DATE_STYLE = 'margin:0;font-size:14px;line-height:14px;color:#6b7280'
const TOOLTIP_BODY_STYLE = 'background:#fff;padding:12px;display:flex;flex-direction:column;gap:14px'
const TOOLTIP_ROW_STYLE = 'display:flex;align-items:center;gap:6px;font-size:14px;color:#111928'
const TOOLTIP_LABEL_STYLE = 'color:#6b7280'
const dotStyle = color =>
  `width:8px;height:8px;border-radius:50%;background:${color};display:inline-block`

const buildTooltipRow = ({ color, label, formatted }) => `
  <div style="${TOOLTIP_ROW_STYLE}">
    <span style="${dotStyle(color)}"></span>
    <span><span style="${TOOLTIP_LABEL_STYLE}">${label}:</span> ${formatted}</span>
  </div>
`

const buildTooltipCustom = (fullLabels, valueSuffix) => ({ series, dataPointIndex, w: chart }) => {
  const date = fullLabels[dataPointIndex] || ''
  const rows = chart.globals.seriesNames.map((name, idx) => {
    const value = series[idx]?.[dataPointIndex]
    return buildTooltipRow({
      color: chart.globals.colors[idx],
      label: name,
      formatted: Number.isFinite(value) ? `${value}${valueSuffix}` : '-'
    })
  }).join('')
  return `
    <div style="${TOOLTIP_WRAPPER_STYLE}">
      <div style="${TOOLTIP_HEADER_STYLE}"><p style="${TOOLTIP_DATE_STYLE}">${date}</p></div>
      <div style="${TOOLTIP_BODY_STYLE}">${rows}</div>
    </div>
  `
}

const POINT_MARKER_SIZE = 4.5
const POINT_GUIDE_COLOR = '#4b5563'
const POINT_GUIDE_DASH = 5

// An area or line series holding a single bucket draws no visible segment.
// The marker and its dashed guide show where the period sits, instead of
// Leaving a chart that reads as empty.
export const singlePointOptions = buckets => {
  if (buckets.length !== 1)
    return { markers: { size: 0 }, annotations: { xaxis: [] } }
  return {
    markers: {
      size: POINT_MARKER_SIZE,
      strokeWidth: 0,
      hover: { size: POINT_MARKER_SIZE, sizeOffset: 0 }
    },
    annotations: {
      xaxis: [{
        // `x` names the annotated category in the ApexCharts annotation API.
        /* eslint-disable-next-line id-length */
        x: buckets[0].label,
        borderColor: POINT_GUIDE_COLOR,
        strokeDashArray: POINT_GUIDE_DASH
      }]
    }
  }
}

export const SINGLE_POINT_TOOLTIP_CLASSES =
  'tw:[&_.apexcharts-tooltip]:left-[calc(var(--chart-grid-center-x)_+_10px)]! '
  + 'tw:[&_.apexcharts-tooltip]:top-[var(--chart-grid-center-y)]! '
  + 'tw:[&_.apexcharts-tooltip]:-translate-y-1/2! '
  + 'tw:[&_.apexcharts-tooltip-arrow]:hidden!'

export const storeGridCenter = (chartContext, chart) => {
  const { gridHeight, gridWidth, translateX, translateY } = chart.layout
  const { style } = chart.dom.baseEl
  style.setProperty('--chart-grid-center-x', `${translateX + gridWidth / 2}px`)
  style.setProperty('--chart-grid-center-y', `${translateY + gridHeight / 2}px`)
}

export const withCategories = (baseOptions, buckets, { containerWidth, range, valueSuffix = '' } = {}) => {
  const fullLabels = buckets.map(bucket => bucket.label)
  const sampled = sampleLabels(fullLabels, { containerWidth, font: AXIS_FONT, range })
  return {
    ...baseOptions,
    xaxis: {
      ...baseOptions.xaxis,
      type: 'category',
      tickPlacement: 'on',
      categories: sampled
    },
    tooltip: {
      ...baseOptions.tooltip,
      shared: true,
      intersect: false,
      cssClass: 'tw:chart-tooltip-host!',
      custom: buildTooltipCustom(fullLabels, valueSuffix)
    }
  }
}
